import React, { createRef } from "react";
import ReactDOM from "react-dom";
import {
  BrickWrapper,
  event,
  type EventEmitter,
  property,
  UpdatingElement,
  method,
} from "@next-core/brick-kit";
import type {
  BuilderSnippetNode,
  Storyboard,
  StoryboardContextItem,
} from "@next-core/brick-types";
import {
  ExcuteProxyMethodResult,
  PreviewSettings,
  PreviewDataOption,
  PreviewMessagePreviewContractUpdateDetail,
  PreviewDebugData,
} from "@next-types/preview";
import {
  BuilderProvider,
  EventDetailOfNodeAddStored,
} from "@next-core/editor-bricks-helper";
import {
  CaptureStatus,
  PreviewContainer,
  PreviewerResize,
  type PreviewContainerRef,
} from "./PreviewContainer";

export interface PreviewContainerProps {
  previewUrl?: string;
  appId?: string;
  templateId?: string;
  formId?: string;
  formData?: FormData;
  snippetGraphData?: BuilderSnippetNode[];
  routePath?: string;
  routeId?: string;
  routeExact?: boolean;
  previewSettings?: PreviewSettings;
  inspecting?: boolean;
  viewportWidth?: number;
  viewportHeight?: number;
  screenshotMaxWidth?: number;
  screenshotMaxHeight?: number;
  onPreviewStart?: (event: CustomEvent) => void;
  onExcuteProxyMethodSuccess?: (event: CustomEvent) => void;
  onExcuteProxyMethodError?: (event: CustomEvent) => void;
  onPreviewDebug?: (event: CustomEvent) => void;
  onMatchApiCache?: (event: CustomEvent) => void;
  onUrlChange?: (event: CustomEvent) => void;
  onScaleChange?: (event: CustomEvent) => void;
  onRouteMatch?: (event: CustomEvent) => void;
  onCapturestatusChange?: (event: CustomEvent) => void;
  onScreenshotCapture?: (event: CustomEvent) => void;
  onPreviewDrop?: (event: CustomEvent) => void;
  onDataModelDrop?: (event: CustomEvent) => void;
  onPreviewResize?: (event: CustomEvent) => void;
  onInspectSingleDataValueSuccess?: (event: CustomEvent) => void;
  onInspectAllDataValuesSuccess?: (event: CustomEvent) => void;
  onInspectDataValueError?: (event: CustomEvent) => void;
  onDebugDataValueSuccess?: (event: CustomEvent) => void;
  onDebugDataValueError?: (event: CustomEvent) => void;
  onInspectRuntimeValue?: (event: CustomEvent) => void;
  onContractUpdate?: (event: CustomEvent) => void;
}

export interface PreviewContainerElementProps {
  previewUrl?: string;
  appId?: string;
  templateId?: string;
  formId?: string;
  formData?: FormData;
  snippetGraphData?: BuilderSnippetNode[];
  routePath?: string;
  routeId?: string;
  routeExact?: boolean;
  previewSettings?: PreviewSettings;
  inspecting?: boolean;
  viewportWidth?: number;
  viewportHeight?: number;
  screenshotMaxWidth?: number;
  screenshotMaxHeight?: number;
  onPreviewStart?: (event: CustomEvent<any>) => void;
  onExcuteProxyMethodSuccess?: (event: CustomEvent<ExcuteProxyMethodResult>) => void;
  onExcuteProxyMethodError?: (event: CustomEvent<ExcuteProxyMethodResult>) => void;
  onPreviewDebug?: (event: CustomEvent<any[]>) => void;
  onMatchApiCache?: (event: CustomEvent<number>) => void;
  onUrlChange?: (event: CustomEvent<string>) => void;
  onScaleChange?: (event: CustomEvent<number>) => void;
  onRouteMatch?: (event: CustomEvent<boolean>) => void;
  onCapturestatusChange?: (event: CustomEvent<CaptureStatus>) => void;
  onScreenshotCapture?: (event: CustomEvent<Blob>) => void;
  onPreviewDrop?: (event: CustomEvent<Record<string, any>>) => void;
  onDataModelDrop?: (event: CustomEvent<Record<string, any>>) => void;
  onPreviewResize?: (event: CustomEvent<PreviewerResize>) => void;
  onInspectSingleDataValueSuccess?: (event: CustomEvent<unknown>) => void;
  onInspectAllDataValuesSuccess?: (event: CustomEvent<Map<string, StoryboardContextItem>>) => void;
  onInspectDataValueError?: (event: CustomEvent<unknown>) => void;
  onDebugDataValueSuccess?: (event: CustomEvent<unknown>) => void;
  onDebugDataValueError?: (event: CustomEvent<unknown>) => void;
  onInspectRuntimeValue?: (event: CustomEvent<unknown>) => void;
  onContractUpdate?: (event: CustomEvent<any>) => void;
}

/**
 * @id next-builder.preview-container
 * @author bot
 * @history
 * 1.x.0: 新增构件 `next-builder.preview-container`
 * @docKind brick
 * @noInheritDoc
 */
export class PreviewContainerElement extends UpdatingElement implements PreviewContainerProps {
  @property()
  previewUrl: string;

  @property()
  appId: string;

  @property()
  templateId: string;

  @property()
  formId: string;

  @property({ attribute: false })
  formData: FormData;

  @property({
    attribute: false,
  })
  snippetGraphData: BuilderSnippetNode[];

  @property()
  routePath: string;

  @property()
  routeId: string;

  @property({ type: Boolean })
  routeExact: boolean;

  @property({ attribute: false })
  previewSettings: PreviewSettings;

  @property({ type: Boolean })
  inspecting: boolean;

  @property({ type: Number })
  viewportWidth: number;

  @property({ type: Number })
  viewportHeight: number;

  @property({ type: Number })
  screenshotMaxWidth: number;

  @property({ type: Number })
  screenshotMaxHeight: number;

  @event({ type: "preview.start" })
  private _previewStartEvent: EventEmitter<void>;

  private _handlePreviewStart = (): void => {
    this._previewStartEvent.emit();
  };

  @event({ type: "excute.proxy.method.success" })
  private _excuteProxyMethodSuccess: EventEmitter<ExcuteProxyMethodResult>;

  private _handleExcuteProxyMethodSuccess = (
    result: ExcuteProxyMethodResult
  ): void => {
    this._excuteProxyMethodSuccess.emit(result);
  };

  @event({ type: "excute.proxy.method.error" })
  private _excuteProxyMethodError: EventEmitter<ExcuteProxyMethodResult>;

  private _handleExcuteProxyMethodError = (
    result: ExcuteProxyMethodResult
  ): void => {
    this._excuteProxyMethodError.emit(result);
  };

  @event({ type: "preview.debug" })
  private _onPreviewDebug: EventEmitter<any[]>;

  private _handlePreviwDebug = (result: any[]): void => {
    this._onPreviewDebug.emit(result);
  };

  @event({ type: "match.api.cache" })
  private _matchApiCacheEvent: EventEmitter<number>;

  private _handleMatchApiCache = (num: number): void => {
    this._matchApiCacheEvent.emit(num);
  };

  @event({ type: "url.change" })
  private _urlChangeEvent: EventEmitter<string>;

  private _handleUrlChange = (url: string): void => {
    this._urlChangeEvent.emit(url);
  };

  @event({ type: "scale.change" })
  private _scaleChangeEvent: EventEmitter<number>;

  private _handleScaleChange = (scale: number): void => {
    this._scaleChangeEvent.emit(scale);
  };

  @event({ type: "route.match" })
  private _routeMatchEvent: EventEmitter<boolean>;

  private _handleRouteMatch = (match: boolean): void => {
    this._routeMatchEvent.emit(match);
  };

  @event({ type: "captureStatus.change" })
  private _captureStatusChangeEvent: EventEmitter<CaptureStatus>;

  private _handleCaptureStatusChange = (status: CaptureStatus): void => {
    this._captureStatusChangeEvent.emit(status);
  };

  @event({ type: "screenshot.capture" })
  private _screenshotCaptureEvent: EventEmitter<Blob>;

  private _handleScreenshotCapture = (screenshot: Blob): void => {
    this._screenshotCaptureEvent.emit(screenshot);
  };

  @event({ type: "preview.drop" })
  private _handlePreviewerDropEvent: EventEmitter<Record<string, any>>;

  @event({ type: "data.model.drop" })
  private _handleDataModelDropEvent: EventEmitter<Record<string, any>>;

  private _handlePreviewerDrop = (params: Record<string, any>): void => {
    if (params.nodeData.dragType === "dataModel") {
      // 基于数据模型的拖拽的场景
      this._handleDataModelDropEvent.emit(params);
    } else {
      this._handlePreviewerDropEvent.emit(params);
    }
  };

  @event({ type: "preview.resize" })
  private _handlePreviewResizeEvent: EventEmitter<PreviewerResize>;

  private _handlePreivewResize = (resize: PreviewerResize): void => {
    this._handlePreviewResizeEvent.emit(resize);
  };

  /**
   * @deprecated Use `inspectDataValueCallback()` instead.
   */
  @event({ type: "inspect.single.data.value.success" })
  private _handleInspectSingleDataValueSuccessEvent: EventEmitter<unknown>;

  private _handleInspectSingleDataValueSuccess = (value: unknown): void => {
    this._handleInspectSingleDataValueSuccessEvent.emit(value);
  };

  /**
   * @deprecated Use `inspectAllDataValueCallback()` instead.
   */
  @event({ type: "inspect.all.data.values.success" })
  private _handleInspectAllDataValuesSuccessEvent: EventEmitter<
    Map<string, StoryboardContextItem>
  >;

  private _handleInspectAllDataValuesSuccess = (
    value: Map<string, StoryboardContextItem>
  ): void => {
    this._handleInspectAllDataValuesSuccessEvent.emit(value);
  };

  /**
   * @deprecated Use `inspectDataValueCallback()` or `inspectAllDataValueCallback()` instead.
   */
  @event({ type: "inspect.data.value.error" })
  private _handleInspectDataValueErrorEvent: EventEmitter<unknown>;

  private _handleInspectDataValueError = (value: unknown): void => {
    this._handleInspectDataValueErrorEvent.emit(value);
  };

  @event({ type: "debug.data.value.success" })
  private _handleDebugDataValueSuccessEvent: EventEmitter<unknown>;

  private _handleDebugDataValueSuccess = (value: unknown): void => {
    this._handleDebugDataValueSuccessEvent.emit(value);
  };

  @event({ type: "debug.data.value.error" })
  private _handleDebugDataValueErrorEvent: EventEmitter<unknown>;

  private _handleDebugDataValueError = (value: unknown): void => {
    this._handleDebugDataValueErrorEvent.emit(value);
  };

  @event({ type: "inspect.runtime.value" })
  private _handleInspectRuntimeValueEvent: EventEmitter<unknown>;

  private _handleInspectRuntimeValue = (value: unknown): void => {
    this._handleInspectRuntimeValueEvent.emit(value);
  };

  @event({ type: "contract.update" })
  private _handleContractUpdateEvent: EventEmitter<any>;
  private _handleContractUpdate = (
    value: PreviewMessagePreviewContractUpdateDetail
  ): void => {
    this._handleContractUpdateEvent.emit(value);
  };

  private _previewContainerRef = createRef<PreviewContainerRef>();

  @method()
  resize(): void {
    this._previewContainerRef.current.resize();
  }

  @method()
  toggleTheme(): void {
    this._previewContainerRef.current.toggleTheme();
  }

  @method()
  refresh(
    appId: string,
    storyboardPatch: Partial<Storyboard>,
    options: Record<string, unknown>
  ): void {
    this._previewContainerRef.current.refresh(appId, storyboardPatch, options);
  }

  @method()
  reload(): void {
    this._previewContainerRef.current.reload();
  }

  @method()
  back(): void {
    this._previewContainerRef.current.back();
  }

  @method()
  forward(): void {
    this._previewContainerRef.current.forward();
  }

  @method()
  capture(): void {
    this._previewContainerRef.current.capture();
  }

  // istanbul ignore next
  /**
   * @deprecated Use `inspectDataValueCallback()` instead.
   */
  @method()
  inspectDataValue(name: string, option: PreviewDataOption): any {
    return this._previewContainerRef.current.inspectDataValue(name, option);
  }

  // istanbul ignore next
  /**
   * @deprecated Use `inspectAllDataValueCallback()` instead.
   */
  @method()
  inspectAllDataValue(option: PreviewDataOption): any {
    return this._previewContainerRef.current.inspectDataValue(
      undefined,
      option
    );
  }

  // istanbul ignore next
  @method()
  inspectDataValueCallback(
    name: string,
    option: PreviewDataOption
  ): Promise<unknown> {
    return this._previewContainerRef.current.inspectDataValueCallback(
      name,
      option
    );
  }

  // istanbul ignore next
  @method()
  inspectAllDataValueCallback(option: PreviewDataOption): Promise<unknown> {
    return this._previewContainerRef.current.inspectDataValueCallback(
      undefined,
      option
    );
  }

  // istanbul ignore next
  @method()
  debugDataValue(
    debugData: PreviewDebugData,
    options: PreviewDataOption
  ): void {
    this._previewContainerRef.current.debugDataValue(debugData, options);
  }

  // istanbul ignore next
  @method()
  nodeAddStored(detail: EventDetailOfNodeAddStored): void {
    this._previewContainerRef.current.manager.nodeAddStored(detail);
  }

  @method()
  excuteProxyMethod(ref: string, method: string, args?: any[]): void {
    this._previewContainerRef.current.excuteProxyMethod(ref, method, args);
  }

  /** @deprecated */
  @method()
  updatePreviewUrl(url: string): void {
    //
  }

  connectedCallback(): void {
    // Don't override user's style settings.
    // istanbul ignore else
    if (!this.style.display) {
      this.style.display = "block";
    }
    this._render();
  }

  disconnectedCallback(): void {
    ReactDOM.unmountComponentAtNode(this);
  }

  protected _render(): void {
    // istanbul ignore else
    if (this.isConnected) {
      ReactDOM.render(
        <BrickWrapper>
          <BuilderProvider>
            <PreviewContainer
              ref={this._previewContainerRef}
              previewUrl={this.previewUrl}
              appId={this.appId}
              routeId={this.routeId}
              templateId={this.templateId}
              formId={this.formId}
              formData={this.formData}
              snippetGraphData={this.snippetGraphData}
              routePath={this.routePath}
              routeExact={this.routeExact}
              previewSettings={this.previewSettings}
              inspecting={this.inspecting}
              viewportWidth={this.viewportWidth}
              viewportHeight={this.viewportHeight}
              screenshotMaxWidth={this.screenshotMaxWidth}
              screenshotMaxHeight={this.screenshotMaxHeight}
              onPreviewStart={this._handlePreviewStart}
              onUrlChange={this._handleUrlChange}
              onScaleChange={this._handleScaleChange}
              onRouteMatch={this._handleRouteMatch}
              onCaptureStatusChange={this._handleCaptureStatusChange}
              onScreenshotCapture={this._handleScreenshotCapture}
              onPreviewerDrop={this._handlePreviewerDrop}
              onPreviewerResize={this._handlePreivewResize}
              onInspectSingleDataValueSuccess={
                this._handleInspectSingleDataValueSuccess
              }
              onInspectAllDataValuesSuccess={
                this._handleInspectAllDataValuesSuccess
              }
              onInspectDataValueError={this._handleInspectDataValueError}
              onContractUpdate={this._handleContractUpdate}
              onExcuteProxyMethodSuccess={this._handleExcuteProxyMethodSuccess}
              onExcuteProxyMethodError={this._handleExcuteProxyMethodError}
              onPreviewDebug={this._handlePreviwDebug}
              onMatchApiCache={this._handleMatchApiCache}
              onDebugValueSuccess={this._handleDebugDataValueSuccess}
              onDebugValueError={this._handleDebugDataValueError}
              onRuntimeDataValue={this._handleInspectRuntimeValue}
            />
          </BuilderProvider>
        </BrickWrapper>,
        this
      );
    }
  }
}

customElements.define(
  "next-builder.preview-container",
  PreviewContainerElement
);
